import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Terms
export const termsAPI = {
  getAll: () => api.get('/terms'),
  getActive: () => api.get('/terms/active'),
  create: (data) => api.post('/terms', data),
  update: (id, data) => api.put(`/terms/${id}`, data),
  delete: (id) => api.delete(`/terms/${id}`),
};

// Rooms
export const roomsAPI = {
  getAll: () => api.get('/rooms'),
  create: (data) => api.post('/rooms', data),
  update: (id, data) => api.put(`/rooms/${id}`, data),
  delete: (id) => api.delete(`/rooms/${id}`),
};

// Classes
export const classesAPI = {
  getAll: (category, includeHidden = false) => api.get('/classes', { params: { category, include_hidden: includeHidden } }),
  getAllForAdmin: (category) => api.get('/classes', { params: { category, include_hidden: true, include_images: true } }),
  getOne: (id) => api.get(`/classes/${id}`),
  getImage: (id) => api.get(`/classes/${id}/image`),
  create: (data) => api.post('/classes', data),
  update: (id, data) => api.put(`/classes/${id}`, data),
  delete: (id) => api.delete(`/classes/${id}`),
};

// Schedules
export const schedulesAPI = {
  getAll: (termId, classId, includeHidden = false) => api.get('/schedules', { params: { term_id: termId, class_id: classId, include_hidden: includeHidden } }),
  getAllForAdmin: (termId, classId) => api.get('/schedules', { params: { term_id: termId, class_id: classId, include_hidden: true } }),
  getOne: (id) => api.get(`/schedules/${id}`),
  create: (data) => api.post('/schedules', data),
  delete: (id) => api.delete(`/schedules/${id}`),
};

// Students
export const studentsAPI = {
  add: (data) => api.post('/students/add', data),
  importSingle: (data) => api.post('/students/add', data), // Alias for single student import
  addSiblings: (data) => api.post('/students/add-siblings', data), // Batch add siblings with combined email
  get: (id) => api.get(`/students/${id}`),
  update: (id, data) => api.put(`/students/${id}`, data),
  updateDiscount: (id, data) => api.put(`/students/${id}/discount`, data),
  updateMailingLists: (id, lists) => api.put(`/students/${id}/mailing-lists`, { mailing_lists: lists }),
  import: (data) => api.post('/students/import', data),
  sendInvites: (studentIds) => api.post('/students/send-invites', { student_ids: studentIds }),
  // Add classes to existing student (with optional pro-rating for mid-term joining and pricing options)
  addClasses: (studentId, classIds, paymentStatus = 'pending', isProRated = false, weeksRemaining = null, pricingOptions = {}) => 
    api.post(`/students/${studentId}/add-classes`, { 
      class_ids: classIds, 
      payment_status: paymentStatus,
      is_pro_rated: isProRated,
      weeks_remaining: weeksRemaining,
      pricing_options: pricingOptions  // { classId: 'term' | 'one_off' }
    }),
};

// Bookings
export const bookingsAPI = {
  getAll: () => api.get('/bookings'),
  getGrouped: () => api.get('/bookings/grouped'),  // Groups by student for admin view
  getBySchedule: (scheduleId, date) => api.get(`/bookings/schedule/${scheduleId}`, { params: { date } }),
  create: (data) => api.post('/bookings', data),
  cancel: (id) => api.put(`/bookings/${id}/cancel`),
  adminDelete: (id) => api.delete(`/bookings/admin/${id}`),
  updateAmount: (id, amount) => api.patch(`/bookings/admin/${id}/amount?amount=${amount}`),
  markAsPaid: (id) => api.patch(`/bookings/admin/${id}/mark-paid`),
};

// Enrollments (multi-class)
export const enrollmentsAPI = {
  create: (data) => api.post('/enrollments', data),
};

// Waitlist
export const waitlistAPI = {
  getAll: (scheduleId) => api.get('/waitlist', { params: { schedule_id: scheduleId } }),
  getMine: () => api.get('/waitlist/my'),
  join: (data) => api.post('/waitlist', data),
  leave: (id) => api.delete(`/waitlist/${id}`),
  // Admin endpoints
  getAdminList: () => api.get('/waitlist/admin'),
  notify: (id) => api.post(`/waitlist/${id}/notify`),
  adminRemove: (id) => api.delete(`/waitlist/admin/${id}`),
};

// Attendance
export const attendanceAPI = {
  get: (scheduleId, date) => api.get(`/attendance/${scheduleId}/${date}`),
  mark: (data) => api.post('/attendance', data),
};

// Payments
export const paymentsAPI = {
  createCheckout: (bookingId, originUrl) => 
    api.post('/payments/checkout', { booking_id: bookingId, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getStripeStatus: () => api.get('/payments/stripe-status'),
  // Card Saving
  saveCard: (originUrl) => api.post('/payments/save-card', { origin_url: originUrl }),
  confirmCardSaved: () => api.post('/payments/confirm-card-saved'),
  getMySavedCard: () => api.get('/payments/my-saved-card'),
  // Admin Payment Authorization
  getPendingPayments: () => api.get('/payments/pending-payments'),
  takePayment: (bookingIds) => api.post('/payments/take-payment', { booking_ids: bookingIds }),
  // Scheduled Bulk Payments
  scheduleBulkPayment: (data) => api.post('/payments/schedule-bulk-payment', data),
  getScheduledPayments: () => api.get('/payments/scheduled-payments'),
  getScheduledPaymentsHistory: () => api.get('/payments/scheduled-payments/history'),
  cancelScheduledPayment: (scheduleId) => api.delete(`/payments/scheduled-payments/${scheduleId}`),
  processScheduledPaymentNow: (scheduleId) => api.post(`/payments/scheduled-payments/${scheduleId}/process`),
  // Payment Methods
  getPaymentMethods: () => api.get('/payments/payment-methods'),
  setDefaultPaymentMethod: (paymentMethodId) => api.post(`/payments/payment-methods/set-default/${paymentMethodId}`),
  deletePaymentMethod: (paymentMethodId) => api.delete(`/payments/payment-methods/${paymentMethodId}`),
  addPaymentMethodSession: () => api.post('/payments/payment-methods/add'),
  // One-Off Bookings
  getAvailableOneOffClasses: () => api.get('/payments/one-off/available-classes'),
  createOneOffCheckout: (data) => api.post('/payments/one-off/checkout', data),
  getOneOffBookings: () => api.get('/payments/one-off/bookings'),
};

// Teachers
export const teachersAPI = {
  getAll: () => api.get('/teachers'),
  getSchedule: () => api.get('/teacher/schedule'),
  getClasses: () => api.get('/teacher/classes'),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRevenue: () => api.get('/dashboard/revenue'),
  getDetailedRevenue: (startDate, endDate) => api.get('/dashboard/revenue/detailed', { 
    params: { start_date: startDate, end_date: endDate } 
  }),
  getEnrollmentTrends: () => api.get('/dashboard/enrollment-trends'),
  getClassPopularity: () => api.get('/dashboard/class-popularity'),
  getAttendanceSummary: () => api.get('/dashboard/attendance-summary'),
  updateExpenses: (totalExpenses) => api.put('/dashboard/expenses', { total_expenses: totalExpenses }),
  getTopClients: (limit = 10) => api.get('/dashboard/top-clients', { params: { limit } }),
  
  // Audit Logs
  getAuditLogs: (params = {}) => api.get('/audit-logs', { params }),
  getEntityAuditTrail: (entityType, entityId) => api.get(`/audit-logs/${entityType}/${entityId}`),
};

// Enrolments (State-driven trial → enrolment lifecycle)
export const enrolmentsAPI = {
  // Trials
  getTrials: (status) => api.get('/trials', { params: { status } }),
  getTrialsThisWeek: () => api.get('/trials/this-week'),
  markTrialAttendance: (trialId, attended, notes) => api.put(`/trials/${trialId}/attendance`, { 
    trial_id: trialId, attended, notes 
  }),
  sendClassOffer: (trialId, classIds, message) => api.post(`/trials/${trialId}/send-offer`, {
    trial_id: trialId, class_ids: classIds, message
  }),
  
  // Enrolments
  getAll: (status, classId) => api.get('/enrolments', { params: { status, class_id: classId } }),
  create: (studentId, classId, billingPlan, trialId) => api.post('/enrolments/create', null, {
    params: { student_id: studentId, class_id: classId, billing_plan: billingPlan, trial_id: trialId }
  }),
  activateBilling: (enrolmentId) => api.put(`/enrolments/${enrolmentId}/activate-billing`),
  getPendingBilling: () => api.get('/enrolments/pending-billing'),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Contact & Free Trial Forms
export const formsAPI = {
  submitContact: (data) => api.post('/contact', data),
  submitFreeTrial: (data) => api.post('/free-trial', data),
  submitFreeTrialMulti: (data) => api.post('/free-trial-multi', data),
};

// Portal Messages (Admin to Parent communication)
export const messagesAPI = {
  getAll: () => api.get('/messages'),
  send: (data) => api.post('/messages', data),
  markRead: (id) => api.post(`/messages/${id}/read`),
  reply: (id, data) => api.post(`/messages/${id}/reply`, { message_id: id, content: data.content }),
  getReplies: (id) => api.get(`/messages/${id}/replies`),
  resendUnread: (id) => api.post(`/messages/${id}/resend-unread`),
  readReceipts: (id) => api.get(`/messages/${id}/read-receipts`),
  exportRecipients: (data) => api.post('/messages/export-recipients', data),
};

// Noticeboard
export const noticesAPI = {
  getAll: () => api.get('/notices'),
  create: (data) => api.post('/notices', data),
  update: (id, data) => api.put(`/notices/${id}`, data),
  delete: (id) => api.delete(`/notices/${id}`),
  markRead: (id) => api.post(`/notices/${id}/read`),
  readReceipts: (id) => api.get(`/notices/${id}/read-receipts`),
};

// Daily Register/Attendance
export const registerAPI = {
  getToday: () => api.get('/register/today'),
  signIn: (bookingId, isLate = false, arrivalTime = null) => 
    api.post('/register/signin', { booking_id: bookingId, is_late: isLate, arrival_time: arrivalTime }),
  signOut: (bookingId) => api.post('/register/signout', { booking_id: bookingId }),
  undoSignIn: (bookingId) => api.post('/register/undo-signin', { booking_id: bookingId }),
  markLate: (bookingId, arrivalTime) => api.post('/register/mark-late', { booking_id: bookingId, arrival_time: arrivalTime }),
  getHistory: (startDate, endDate) => api.get('/register/history', { params: { start_date: startDate, end_date: endDate } }),
};

// Absence Reporting
export const absenceAPI = {
  report: (data) => api.post('/absences/report', data),
  getAll: () => api.get('/absences'),
  getForDate: (date) => api.get(`/absences/date/${date}`),
  cancel: (id) => api.delete(`/absences/${id}`),
};

// Parent Portal APIs
export const parentAPI = {
  getChildren: () => api.get('/auth/my-children'),
  updateChild: (studentName, data) => api.put(`/auth/my-children/${encodeURIComponent(studentName)}`, data),
  getAccountBalance: () => api.get('/auth/my-account-balance'),
};

// Accident Reports
export const accidentReportsAPI = {
  getAll: () => api.get('/students/accident-reports'),
  create: (data) => api.post('/students/accident-reports', data),
  sign: (id, data) => api.post(`/students/accident-reports/${id}/sign`, data),
  requestAcknowledgment: (id) => api.post(`/students/accident-reports/${id}/request-acknowledgment`),
  acknowledge: (id) => api.post(`/students/accident-reports/${id}/acknowledge`),
};

// Invoicing & Payments
export const invoicesAPI = {
  getAll: () => api.get('/payments/invoices'),
  getById: (id) => api.get(`/payments/invoices/${id}`),
  download: (id) => api.get(`/payments/invoices/${id}/download`),
  downloadPDF: (id) => api.get(`/payments/invoices/${id}/pdf`, { responseType: 'blob' }),
  authorize: (bookingIds) => api.post('/payments/authorize', { booking_ids: bookingIds }),
  refund: (paymentId, reason) => api.post('/payments/refund', { payment_id: paymentId, reason }),
  getLatePayments: () => api.get('/payments/late-payments'),
  getRefunds: () => api.get('/payments/refunds'),
};

// Discounts & Scholarships
export const discountsAPI = {
  getAll: () => api.get('/discounts'),
  getActive: () => api.get('/discounts/active'),
  create: (data) => api.post('/discounts', data),
  update: (id, data) => api.put(`/discounts/${id}`, data),
  delete: (id) => api.delete(`/discounts/${id}`),
  validate: (code, studentId, classId) => api.post('/discounts/validate', null, { 
    params: { code, student_id: studentId, class_id: classId } 
  }),
  apply: (discountId, bookingId) => api.post('/discounts/apply', null, { 
    params: { discount_id: discountId, booking_id: bookingId } 
  }),
  getStudentDiscounts: (studentName) => api.get(`/discounts/student/${encodeURIComponent(studentName)}`),
  // Scholarships
  getScholarships: () => api.get('/discounts/scholarships'),
  applyForScholarship: (data) => api.post('/discounts/scholarships/apply', data),
  reviewScholarship: (id, action, discountPercent, notes) => api.put(
    `/discounts/scholarships/${id}/review`, 
    null, 
    { params: { action, approved_discount_percent: discountPercent, notes } }
  ),
};

// Mailing Lists
export const mailingListsAPI = {
  getAll: () => api.get('/mailing-lists'),
  create: (data) => api.post('/mailing-lists', data),
  delete: (name) => api.delete(`/mailing-lists/${encodeURIComponent(name)}`),
  getStudents: (name) => api.get(`/mailing-lists/${encodeURIComponent(name)}/students`),
  addStudents: (name, studentIds) => api.post(`/mailing-lists/${encodeURIComponent(name)}/add-students`, { student_ids: studentIds }),
  removeStudents: (name, studentIds) => api.post(`/mailing-lists/${encodeURIComponent(name)}/remove-students`, { student_ids: studentIds }),
};

// Media Drop
export const mediaAPI = {
  getAll: (folder, fileType) => api.get('/media', { params: { folder, file_type: fileType } }),
  getFolders: () => api.get('/media/folders'),
  upload: (data) => api.post('/media', data),
  delete: (id) => api.delete(`/media/${id}`),
  getPracticeMaterials: () => api.get('/media/practice-materials'),
};

// Site Media Management
export const siteMediaAPI = {
  // Get all media settings
  getSettings: () => api.get('/site-media/settings'),
  getPageSettings: (page) => api.get(`/site-media/settings/${page}`),
  
  // Update media settings
  updateSetting: (data) => api.put('/site-media/settings', data),
  bulkUpdateSettings: (settings) => api.post('/site-media/settings/bulk', settings),
  
  // File uploads - using XMLHttpRequest for reliable FormData handling
  uploadFile: (file, mediaType) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      if (mediaType) {
        formData.append('media_type', mediaType);
      }
      
      const token = localStorage.getItem('token');
      const xhr = new XMLHttpRequest();
      
      xhr.open('POST', `${API_URL}/api/site-media/upload`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve({ data: JSON.parse(xhr.responseText) });
          } catch (e) {
            reject({ message: 'Invalid response' });
          }
        } else {
          try {
            reject({ response: { data: JSON.parse(xhr.responseText) } });
          } catch (e) {
            reject({ message: xhr.statusText || 'Upload failed' });
          }
        }
      };
      
      xhr.onerror = function() {
        reject({ message: 'Network error' });
      };
      
      xhr.send(formData);
    });
  },
  
  // List uploads
  getUploads: (mediaType) => api.get('/site-media/uploads', { params: { media_type: mediaType } }),
  deleteUpload: (id) => api.delete(`/site-media/uploads/${id}`),
  
  // Seed defaults
  seedDefaults: () => api.post('/site-media/seed-defaults'),
  
  // Gallery
  getGallery: () => api.get('/site-media/gallery'),
  addGalleryImage: (data) => api.post('/site-media/gallery', data),
  updateGalleryImage: (id, data) => api.put(`/site-media/gallery/${id}`, data),
  deleteGalleryImage: (id) => api.delete(`/site-media/gallery/${id}`),
  reorderGallery: (order) => api.post('/site-media/gallery/reorder', order),
  seedGalleryDefaults: () => api.post('/site-media/gallery/seed-defaults'),
};

// Academy Info API (Term dates, events)
export const academyInfoAPI = {
  getTermDates: () => api.get('/academy-info/term-dates'),
  getTrialDates: () => api.get('/academy-info/trial-dates'),
  getTermDateById: (id) => api.get(`/academy-info/term-dates/${id}`),
  createTermDate: (data) => api.post('/academy-info/term-dates', data),
  updateTermDate: (id, data) => api.put(`/academy-info/term-dates/${id}`, data),
  deleteTermDate: (id) => api.delete(`/academy-info/term-dates/${id}`),
};

// VAT Threshold API
export const vatAPI = {
  getProjection: () => api.get('/vat/projection'),
  getHistorical: () => api.get('/vat/historical'),
  addHistoricalEntry: (data) => api.post('/vat/historical', data),
  updateHistoricalEntry: (id, data) => api.put(`/vat/historical/${id}`, data),
  deleteHistoricalEntry: (id) => api.delete(`/vat/historical/${id}`),
};

// Email Logs API (Admin)
export const emailLogsAPI = {
  getLogs: (limit = 100) => api.get(`/admin/email-logs?limit=${limit}`),
};

export default api;
